import React, { useState, useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { STATUSES, OFFERS, TICK_TYPES, CHROME_IMAGES } from "@/data/mockData";
import { agentsApi, contactsApi, campaignsApi } from "@/lib/api";
import {
  PageHeader,
  StatusDot,
  MaturityBadge,
  StatusBadge,
  ScoreBadge,
  OfferBadge,
  EditorialButton,
  SectionTitle,
  Pill,
} from "@/lib/editorial";
import {
  Pause, Play, ChevronLeft, Compass, Play as RunIcon, ArrowRight, AlertCircle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtCET } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AgentDetail() {
  const { id } = useParams();
  const qc = useQueryClient();

  const { data: agent, isLoading, isError } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const { data: contactsData } = useQuery({
    queryKey: ["contacts", id],
    queryFn: () => contactsApi.list(id, { page: 1 }),
    enabled: !!id,
  });

  const contacts = contactsData?.contacts ?? [];

  const [maturity, setMaturity] = useState("cold");
  const [persona, setPersona] = useState("");
  const [offers, setOffers] = useState(new Set());
  const [limits, setLimits] = useState({ dm: 5, invite: 10, like: 50, comment: 10, dmSpacing: 300, inviteSpacing: 600 });
  const [tickType, setTickType] = useState("classify");
  const [liAtDraft, setLiAtDraft] = useState("");
  const [jsessionidDraft, setJsessionidDraft] = useState("");
  const [campaignConfig, setCampaignConfig] = useState({ coffee_date: false, partnership: false, internship: false });
  const [campaignAlloc, setCampaignAlloc] = useState({ offers: 100, coffee_date: 0, partnership: 0, internship: 0 });

  const { data: campaignData } = useQuery({
    queryKey: ["campaigns", id],
    queryFn: () => campaignsApi.get(id),
    enabled: !!id,
  });

  useEffect(() => {
    if (campaignData) {
      setCampaignConfig({ coffee_date: false, partnership: false, internship: false, ...campaignData.config });
      setCampaignAlloc({ offers: 100, coffee_date: 0, partnership: 0, internship: 0, ...campaignData.allocation });
    }
  }, [campaignData]);

  const campaignsMut = useMutation({
    mutationFn: () => campaignsApi.save(id, campaignConfig, campaignAlloc),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["campaigns", id] }); toast("Campaign settings saved."); },
    onError: (e) => toast.error(e.message || "Save failed."),
  });

  // Sync local state when agent loads
  useEffect(() => {
    if (agent) {
      setMaturity(agent.maturity ?? "cold");
      setPersona(agent.persona ?? "");
      setOffers(new Set(agent.offers ?? []));
      setLimits(agent.limits ?? limits);
    }
  }, [agent?.id]); // eslint-disable-line

  const pauseMut = useMutation({ mutationFn: () => agentsApi.pause(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent", id] }); toast("Agent paused."); } });
  const resumeMut = useMutation({ mutationFn: () => agentsApi.resume(id), onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent", id] }); toast("Agent resumed."); } });
  const limitsMut = useMutation({ mutationFn: (body) => agentsApi.saveLimits(id, body), onSuccess: () => { qc.invalidateQueries({ queryKey: ["agent", id] }); toast("Limits saved."); } });
  const personaMut = useMutation({ mutationFn: (body) => agentsApi.savePersona(id, body), onSuccess: () => toast("Persona saved.") });
  const offersMut = useMutation({ mutationFn: (list) => agentsApi.saveDiscoverOffers(id, list), onSuccess: () => toast("Offer selection saved.") });
  const tickMut = useMutation({ mutationFn: (tt) => agentsApi.runTick(id, tt), onSuccess: (_, tt) => toast(`Tick "${tt}" queued.`) });
  const refreshSessionMut = useMutation({
    mutationFn: () => agentsApi.refreshSession(id, liAtDraft, jsessionidDraft || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agent", id] });
      toast("Session refreshed — agent resumed.");
      setLiAtDraft(""); setJsessionidDraft("");
    },
    onError: (e) => toast.error(e.message || "Refresh failed."),
  });

  if (isLoading) return <div className="px-8 py-12 text-[var(--muted-light)]">Loading…</div>;
  if (isError || !agent) return <Navigate to="/admin/agents" replace />;

  const active = agent.active;

  return (
    <div className="text-[var(--ink)]">
      {/* Breadcrumb / header */}
      <div className="px-8 lg:px-14 pt-10 pb-8 border-b border-[var(--rule-light)]">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-6">
          <Link to="/admin/agents" className="hover:text-[var(--ink)] flex items-center gap-1.5">
            <ChevronLeft className="w-3.5 h-3.5" /> Agents
          </Link>
          <span>/</span>
          <span className="text-[var(--ink)]">{agent.name}</span>
          <span className="flex items-center gap-1.5 ml-2">
            <StatusDot active={active} />
            <span>{active ? "Active" : "Paused"}</span>
          </span>
          <MaturityBadge maturity={maturity} />
        </div>

        <PageHeader
          eyebrow={`Agent #${agent.id}`}
          tape={agent.name.split(" ")[0]}
          rest={agent.name.split(" ").slice(1).join(" ") || "agent"}
          sub={agent.persona}
          testId="agent-detail-title"
        />

        {/* Action bar */}
        <div className="flex items-center gap-3 flex-wrap mt-2">
          {active ? (
            <EditorialButton variant="secondary" onClick={() => pauseMut.mutate()} data-testid="agent-detail-pause">
              <Pause className="w-3.5 h-3.5" /> Pause
            </EditorialButton>
          ) : (
            <EditorialButton variant="secondary" onClick={() => resumeMut.mutate()} data-testid="agent-detail-resume">
              <Play className="w-3.5 h-3.5" /> Resume
            </EditorialButton>
          )}

          <div className="flex items-center border border-[var(--ink)]/25">
            <Select value={tickType} onValueChange={setTickType}>
              <SelectTrigger className="border-0 rounded-none w-[140px] h-9 text-[11px] uppercase tracking-[0.18em] focus:ring-0">
                <SelectValue placeholder="Tick" />
              </SelectTrigger>
              <SelectContent>
                {(TICK_TYPES || ["classify","invite","relationship_check","dm","send","discover"]).map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              className="bg-[var(--ink)] text-white px-4 h-9 text-[11px] uppercase tracking-[0.22em] flex items-center gap-2"
              data-testid="agent-detail-run-tick"
              onClick={() => tickMut.mutate(tickType)}
            >
              <RunIcon className="w-3.5 h-3.5" /> Run tick
            </button>
          </div>

          <EditorialButton
            variant="secondary"
            data-testid="agent-detail-run-discovery"
            onClick={() => { agentsApi.runTick(id, "discover"); toast("Discovery queued."); }}
          >
            <Compass className="w-3.5 h-3.5" /> Run Discovery
          </EditorialButton>

          <a href="#limits" className="text-[11px] uppercase tracking-[0.22em] underline-offset-4 underline ml-2">
            Limits ↓
          </a>

          <div className="ml-auto flex items-center gap-3">
            <Link
              to={`/admin/agents/${agent.id}/contacts`}
              className="text-[12px] uppercase tracking-[0.22em] hover:text-[var(--blue)] flex items-center gap-1"
              data-testid="agent-detail-contacts-link"
            >
              All contacts <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to={`/admin/agents/${agent.id}/templates`}
              className="text-[12px] uppercase tracking-[0.22em] hover:text-[var(--blue)] flex items-center gap-1"
              data-testid="agent-detail-templates-link"
            >
              Templates <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {!active && agent.pauseReason && (
          <div className="mt-6 inline-flex items-center gap-2 border border-amber-300 bg-amber-50 px-4 py-2 text-[12px] text-amber-800">
            <AlertCircle className="w-4 h-4" /> Pause reason: {agent.pauseReason}
          </div>
        )}
      </div>

      {/* Stats dark band */}
      <section className="bg-[var(--ink)] text-white px-8 lg:px-14 py-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full opacity-50 hidden lg:block">
          <img src={CHROME_IMAGES.headerDecor} alt="" className="chrome-img w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[var(--ink)]/40 to-[var(--ink)]" />
        </div>
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.24em] text-white/55 mb-4">Section 02 — Live pulse</div>
          <h2 className="font-display uppercase text-[44px] leading-none">
            <span className="tape dark">Stats</span>{" "}
            <span className="italic">today</span>
          </h2>
          <div className="mt-4 h-[2px] w-16 bg-[var(--blue)]" />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-10 bg-white/10">
            <BigStat label="Total contacts" value={(agent.stats?.totalContacts ?? 0).toLocaleString()} />
            <BigStat label="Queued msgs" value={agent.stats?.queuedMsgs ?? 0} />
            <BigStat label="Sent today" value={agent.stats?.sentToday ?? 0} />
            <BigStat label="Last tick" value={agent.stats?.lastTick ? fmtCET(agent.stats.lastTick) : "—"} />
          </div>

          <div className="mt-8 flex flex-wrap gap-2">
            {STATUSES.filter((s) => (agent.statusBreakdown?.[s] ?? 0) > 0).map((s) => (
              <span key={s} className="flex items-center gap-1.5">
                <StatusBadge status={s} />
                <span className="text-[11px] font-mono text-white/70">{agent.statusBreakdown[s]}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Settings, limits, persona */}
      <section className="px-8 lg:px-14 py-12 grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-7">
          <SectionTitle eyebrow="Section 03">Settings</SectionTitle>

          <dl className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-10 text-[14px]">
            <ReadRow label="ID" value={`#${agent.id}`} />
            <ReadRow label="Maturity" value={maturity} />
            <ReadRow label="Tick schedule" value={`${agent.tickSchedule} min`} />
            <ReadRow label="Active" value={active ? "Yes" : "No"} />
            <ReadRow label="LinkedIn email" value={agent.linkedinEmail || "—"} mono />
            <ReadRow
              label="Session"
              value={agent.sessionValid ? "Valid" : agent.hasSessionCookie ? "Expired — needs refresh" : "Not set"}
              mono
            />
          </dl>

          <div className="mt-8 border border-[var(--ink)]/15 p-4 bg-[var(--paper-2)]">
            <Label>Refresh session</Label>
            <p className="text-[12px] text-[var(--muted-light)] mt-1 mb-3">
              Log into this LinkedIn account in a normal browser, open dev tools → Application → Cookies →
              linkedin.com, and paste li_at (required) and JSESSIONID (required for search/discovery to work).
            </p>
            <div className="space-y-2">
              <input
                value={liAtDraft}
                onChange={(e) => setLiAtDraft(e.target.value)}
                placeholder="li_at cookie value"
                data-testid="agent-refresh-li-at"
                className="w-full border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-2 bg-transparent font-mono text-[13px]"
              />
              <input
                value={jsessionidDraft}
                onChange={(e) => setJsessionidDraft(e.target.value)}
                placeholder="JSESSIONID cookie value (optional, needed for search)"
                data-testid="agent-refresh-jsessionid"
                className="w-full border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-2 bg-transparent font-mono text-[13px]"
              />
              <EditorialButton
                variant="secondary"
                disabled={!liAtDraft.trim() || refreshSessionMut.isPending}
                onClick={() => refreshSessionMut.mutate()}
                data-testid="agent-refresh-session-save"
              >
                {refreshSessionMut.isPending ? "Refreshing…" : "Refresh session"}
              </EditorialButton>
            </div>

            <div className="mt-5 pt-4 border-t border-dashed border-[var(--ink)]/15">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-1">
                Or: automatic sync (recommended)
              </div>
              <p className="text-[12px] text-[var(--muted-light)] mb-2">
                Install the UNIDEX Session Sync browser extension and it does the above automatically,
                the moment your session cookie changes — no more manual copy/paste. Configure it with:
              </p>
              <div className="font-mono text-[11px] space-y-1 bg-white border border-[var(--rule-light)] p-3">
                <div>Agent ID: <span className="text-[var(--blue)]">{agent.id}</span></div>
                <div>Server: <span className="text-[var(--blue)]">{window.location.origin}</span></div>
                <div className="break-all">Sync token: <span className="text-[var(--blue)]">{agent.syncToken || "—"}</span></div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <Label>Account persona</Label>
            <textarea
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
              rows={3}
              data-testid="agent-persona"
              className="mt-2 w-full border border-[var(--rule-light)] focus:border-[var(--blue)] outline-none p-3 text-[14px] leading-relaxed"
            />
            <EditorialButton
              className="mt-3"
              variant="secondary"
              onClick={() => personaMut.mutate({ persona, displayName: agent.displayName })}
              data-testid="agent-persona-save"
            >
              Save persona
            </EditorialButton>
          </div>
        </div>

        <div id="limits" className="col-span-12 lg:col-span-5">
          <SectionTitle eyebrow="Section 04">Account maturity &amp; limits</SectionTitle>

          <div className="space-y-5">
            <div>
              <Label>Account maturity</Label>
              <Select value={maturity} onValueChange={setMaturity}>
                <SelectTrigger className="mt-2 rounded-none border-0 border-b border-[var(--ink)]/30 focus:ring-0 h-10 text-[14px]" data-testid="agent-maturity-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cold">cold</SelectItem>
                  <SelectItem value="warm">warm</SelectItem>
                  <SelectItem value="mature">mature</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
              <LimitInput label="DM / day" value={limits.dm} onChange={(v) => setLimits({ ...limits, dm: v })} testId="limit-dm" />
              <LimitInput label="Invite / day" value={limits.invite} onChange={(v) => setLimits({ ...limits, invite: v })} testId="limit-invite" />
              <LimitInput label="Like / day" value={limits.like} onChange={(v) => setLimits({ ...limits, like: v })} testId="limit-like" />
              <LimitInput label="Comment / day" value={limits.comment} onChange={(v) => setLimits({ ...limits, comment: v })} testId="limit-comment" />
              <LimitInput label="DM spacing (s)" value={limits.dmSpacing} onChange={(v) => setLimits({ ...limits, dmSpacing: v })} testId="limit-dm-spacing" />
              <LimitInput label="Invite spacing (s)" value={limits.inviteSpacing} onChange={(v) => setLimits({ ...limits, inviteSpacing: v })} testId="limit-invite-spacing" />
            </div>

            <div className="mt-6 border border-[var(--ink)]/15 p-4 bg-[var(--paper-2)]">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)] mb-3">Effective limits</div>
              <div className="grid grid-cols-3 gap-3 text-[13px] font-mono">
                <Eff label="DM" value={limits.dm} />
                <Eff label="Inv" value={limits.invite} />
                <Eff label="Like" value={limits.like} />
                <Eff label="Comm" value={limits.comment} />
                <Eff label="DM-sp" value={`${limits.dmSpacing}s`} />
                <Eff label="Inv-sp" value={`${limits.inviteSpacing}s`} />
              </div>
            </div>
            <EditorialButton
              onClick={() => limitsMut.mutate({ ...limits, maturity })}
              data-testid="agent-limits-save"
            >
              Save limits
            </EditorialButton>
          </div>
        </div>
      </section>

      {/* Offer targeting */}
      <section className="px-8 lg:px-14 py-12 border-t border-[var(--rule-light)] bg-[var(--paper-2)]">
        <SectionTitle eyebrow="Section 05">Offer targeting</SectionTitle>
        <p className="text-[var(--muted-light)] mb-6 max-w-2xl text-[14px]">
          This agent will discover &amp; message prospects matched to the selected offers.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {OFFERS.map((o) => {
            const checked = offers.has(o.id);
            return (
              <label
                key={o.id}
                className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                  checked ? "border-[var(--blue)] bg-white" : "border-[var(--rule-light)] bg-white hover:border-[var(--ink)]/30"
                }`}
                data-testid={`offer-toggle-${o.id}`}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => {
                    const next = new Set(offers);
                    if (v) next.add(o.id); else next.delete(o.id);
                    setOffers(next);
                  }}
                />
                <span className="font-mono text-[11px] text-[var(--blue)] w-6">#{o.id}</span>
                <span className="font-display text-[16px]">{o.name}</span>
              </label>
            );
          })}
        </div>
        <EditorialButton className="mt-6" onClick={() => offersMut.mutate([...offers])} data-testid="offer-save">
          Save offer selection
        </EditorialButton>
      </section>

      {/* Campaign types */}
      <section className="px-8 lg:px-14 py-12 border-t border-[var(--rule-light)]">
        <SectionTitle eyebrow="Section 06">Additional campaigns</SectionTitle>
        <p className="text-[var(--muted-light)] mb-6 max-w-2xl text-[14px]">
          Run additional outreach types alongside the 12 offers. Each enabled campaign gets its own discovery pool and message template. Set the message budget split below (must total 100%).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {[
            { key: "coffee_date", label: "Coffee Date", desc: "Reach out to interesting founders and tech leaders for a 20-min virtual coffee." },
            { key: "partnership", label: "Partnership", desc: "Propose collaboration, referrals, or white-labelling with other agencies." },
            { key: "internship", label: "Internship", desc: "Request internship or shadowing opportunities at interesting tech companies." },
          ].map(({ key, label, desc }) => (
            <label key={key} className={`flex flex-col gap-2 p-4 border cursor-pointer transition-colors ${campaignConfig[key] ? "border-[var(--blue)] bg-white" : "border-[var(--rule-light)] bg-white hover:border-[var(--ink)]/30"}`}>
              <div className="flex items-center gap-3">
                <Checkbox
                  checked={!!campaignConfig[key]}
                  onCheckedChange={(v) => setCampaignConfig((prev) => ({ ...prev, [key]: !!v }))}
                />
                <span className="font-display text-[16px]">{label}</span>
              </div>
              <p className="text-[12px] text-[var(--muted-light)] leading-relaxed pl-7">{desc}</p>
            </label>
          ))}
        </div>

        {/* Budget allocation */}
        <div className="bg-[var(--paper-2)] border border-[var(--rule-light)] p-5 mb-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] mb-4">Weekly message budget split (%)</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { key: "offers", label: "Offers" },
              { key: "coffee_date", label: "Coffee Date" },
              { key: "partnership", label: "Partnership" },
              { key: "internship", label: "Internship" },
            ].map(({ key, label }) => (
              <div key={key}>
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--muted-light)] mb-1">{label}</div>
                <input
                  type="number" min="0" max="100"
                  value={campaignAlloc[key] ?? 0}
                  onChange={(e) => setCampaignAlloc((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                  disabled={key !== "offers" && !campaignConfig[key]}
                  className="w-full border border-[var(--rule-light)] bg-white focus:border-[var(--blue)] outline-none px-3 py-2 font-mono text-[13px] disabled:opacity-40"
                />
              </div>
            ))}
          </div>
          <div className="mt-3 text-[11px] font-mono text-[var(--muted-light)]">
            Total: {Object.values(campaignAlloc).reduce((a, b) => a + (Number(b) || 0), 0)}%
            {Object.values(campaignAlloc).reduce((a, b) => a + (Number(b) || 0), 0) !== 100 && (
              <span className="text-rose-500 ml-2">Must equal 100%</span>
            )}
          </div>
        </div>

        <EditorialButton onClick={() => campaignsMut.mutate()} disabled={campaignsMut.isPending}>
          {campaignsMut.isPending ? "Saving…" : "Save campaign settings"}
        </EditorialButton>
      </section>

      {/* Recent contacts */}
      <section className="px-8 lg:px-14 py-12">
        <div className="flex items-end justify-between mb-6">
          <SectionTitle eyebrow="Section 07" className="mb-0">Recent contacts</SectionTitle>
          <Link
            to={`/admin/agents/${agent.id}/contacts`}
            className="text-[12px] uppercase tracking-[0.22em] hover:text-[var(--blue)] flex items-center gap-1"
          >
            View all <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <table className="w-full text-[13px]" data-testid="recent-contacts-table">
          <thead>
            <tr className="text-left border-b border-[var(--rule-light)]">
              <Th>Name</Th><Th>Headline</Th><Th>Status</Th><Th>Score</Th><Th>Created</Th>
            </tr>
          </thead>
          <tbody>
            {contacts.slice(0, 8).map((c) => (
              <tr key={c.id} className="border-b border-[var(--rule-light)]">
                <td className="py-3 pr-4 font-display text-[15px]">{c.name}</td>
                <td className="py-3 pr-4 truncate max-w-[360px] text-[var(--muted-light)]">{c.headline}</td>
                <td className="py-3 pr-4"><StatusBadge status={c.status} /></td>
                <td className="py-3 pr-4"><ScoreBadge score={c.score} /></td>
                <td className="py-3 pr-4 font-mono text-[12px] text-[var(--muted-light)]">{c.createdAt?.slice(0, 10)}</td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-[var(--muted-light)] text-center">No contacts yet.</td></tr>
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function ReadRow({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)]">{label}</dt>
      <dd className={`mt-1 text-[14px] ${mono ? "font-mono" : ""}`}>{value}</dd>
    </div>
  );
}
function Label({ children }) {
  return <span className="text-[10px] uppercase tracking-[0.24em] text-[var(--muted-light)]">{children}</span>;
}
function LimitInput({ label, value, onChange, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))}
        className="mt-1 w-full border-b border-[var(--ink)]/30 focus:border-[var(--blue)] outline-none py-2 bg-transparent font-mono text-[14px]"
        data-testid={testId} />
    </div>
  );
}
function Eff({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-[var(--muted-light)] mb-1">{label}</div>
      <div className="font-mono">{value}</div>
    </div>
  );
}
function BigStat({ label, value }) {
  return (
    <div className="bg-[var(--ink)] px-6 py-7">
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/55 mb-2">{label}</div>
      <div className="font-display text-[36px] leading-none">{value}</div>
    </div>
  );
}
function Th({ children, align = "left" }) {
  return <th className={`text-${align} text-[10px] uppercase tracking-[0.22em] text-[var(--muted-light)] py-3 pr-4`}>{children}</th>;
}
